import Service from '../service';
import { firstValueFrom } from 'rxjs';

export default class Lang {
    public value: any;
    private valueToKey: any = {};
    private valuePatterns: any[] = [];
    private indexReady: boolean = false;

    constructor(public service: Service) { }

    public async set(lang: string) {
        this.value = lang;
        try { localStorage.setItem('wiz-lang', lang); } catch(e) {}
        await firstValueFrom(this.service.app.translate.use(lang));
        await this.buildValueIndex();
        await this.service.render();
    }

    private escapeRegExp(value: string) {
        return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private createPattern(value: string, key: string) {
        const names: string[] = [];
        const matcher = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
        let source = '^';
        let cursor = 0;
        let match: RegExpExecArray | null;
        while ((match = matcher.exec(value)) !== null) {
            source += this.escapeRegExp(value.slice(cursor, match.index));
            source += '([\\s\\S]+?)';
            names.push(match[1]);
            cursor = match.index + match[0].length;
        }
        if (!names.length) return null;
        const literal = value.replace(/\{\{\s*[A-Za-z0-9_]+\s*\}\}/g, '');
        // Punctuation-only templates must not match arbitrary names, paths or log text.
        if (!/[A-Za-z가-힣]/.test(literal)) return null;
        source += this.escapeRegExp(value.slice(cursor)) + '$';
        const counterOnly = /^[\s대개명건번초분시간]+$/.test(literal);
        return { key, names, literalLength: literal.replace(/\s/g, '').length, counterOnly, regex: new RegExp(source) };
    }

    private async buildValueIndex() {
        if (this.indexReady) return;
        const translate = this.service.app.translate;
        const [ko, en]: any[] = await Promise.all([
            firstValueFrom(translate.getTranslation('ko')),
            firstValueFrom(translate.getTranslation('en')),
        ]);
        const keys = new Set<string>([
            ...Object.keys(ko || {}),
            ...Object.keys(en || {}),
        ]);
        const aliasPriorities: any = {};
        const patternByValue = new Map<string, any>();
        const priority = (key: string) => key.startsWith('runtime.') ? 3 : key.startsWith('auto.i18n.') ? 1 : 2;

        // A literal catalog key must always resolve to itself, even if an auto-generated
        // entry accidentally contains the same string as one of its translated values.
        keys.forEach((key: string) => { this.valueToKey[key] = key; });
        keys.forEach((key: string) => {
            for (const value of [ko?.[key], en?.[key]]) {
                if (typeof value !== 'string' || value.length === 0) continue;
                if (keys.has(value)) continue;
                const valuePriority = priority(key);
                if ((aliasPriorities[value] || 0) <= valuePriority) {
                    this.valueToKey[value] = key;
                    aliasPriorities[value] = valuePriority;
                }
                const pattern = this.createPattern(value, key);
                const current = patternByValue.get(value);
                if (pattern && (!current || current.priority <= valuePriority)) {
                    patternByValue.set(value, { ...pattern, priority: valuePriority });
                }
            }
            if (key.startsWith('runtime.')) {
                const alias = key.slice(key.lastIndexOf('.') + 1);
                this.valueToKey[alias] = key;
                this.valueToKey[alias.toUpperCase()] = key;
            }
        });
        this.valuePatterns = [...patternByValue.values()]
            .map(({ priority: _priority, ...pattern }: any) => pattern)
            .sort((a: any, b: any) => b.literalLength - a.literalLength
                || a.names.length - b.names.length
                || b.regex.source.length - a.regex.source.length);
        this.indexReady = true;
    }

    public instant(key: string, params: any = {}) {
        return this.service.app.translate.instant(key, params);
    }

    // Preserve supplied data exactly; translate only the UI fallback for a missing value.
    public data(value: any, fallback: any = '') {
        if (value === null || value === undefined || value === '') return this.localize(fallback);
        return value;
    }

    public localize(value: any) {
        if (typeof value !== 'string' || value.length === 0) return value;
        const key = this.valueToKey[value];
        if (key) return this.instant(key);

        const trimmed = value.trim();
        const trimmedKey = this.valueToKey[trimmed];
        if (trimmedKey) {
            const start = value.indexOf(trimmed);
            return value.slice(0, start) + this.instant(trimmedKey) + value.slice(start + trimmed.length);
        }

        for (const pattern of this.valuePatterns) {
            const match = value.match(pattern.regex);
            if (!match) continue;
            if (pattern.counterOnly && match.slice(1).some((part: string) => !/^-?\d[\d,.]*$/.test(part))) continue;
            const params: any = {};
            pattern.names.forEach((name: string, index: number) => {
                params[name] = match[index + 1];
            });
            return this.instant(pattern.key, params);
        }
        return value;
    }

    public get() {
        return this.value;
    }

    public getSaved(): string | null {
        try { return localStorage.getItem('wiz-lang'); } catch(e) { return null; }
    }

    public is(lang: string) {
        return this.value == lang;
    }

    public async translate(key: string) {
        let fn: any = () => new Promise((resolve) => {
            this.service.app.translate.get(key, { value: '' }).subscribe((res: string) => {
                resolve(res);
            });
        })
        return await fn();
    }
}
